<script lang='ts'>
/// <reference types="google.maps" />
import { Loader } from '@googlemaps/js-api-loader';
import { LoadingSpinner } from '@wision/ui';
import MarkerIcon from '../icons/MarkerIcon.vue';
import MapPinIcon from '../icons/MapPinIcon.vue';
import Input from '../InputWithLabel.vue';
import Button from '../Button.vue';
import { useI18n } from 'vue-i18n';
import { useAppState } from '../../store/state';
import { useRouter, useRoute } from 'vue-router';
import { ref } from 'vue';
import { toast } from 'vue-sonner';
import { event } from 'vue-gtag';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBv2WZyARcGV0ct-14PIngxB9VBobEVbaQ';
const MAP_ELEMENT = 'map';

const DEFAULT_LATITUDE = 57.699829;
const DEFAULT_LONGITUDE = 12.828979;
const REDIRECT_DELAY = 2000;

const mapStyling: google.maps.MapTypeStyle[] = [
  {
    stylers: [
      { hue: '#ff1a00' },
      { invert_lightness: true },
      { saturation: -100 },
      { lightness: 33 },
      { gamma: 0.5 }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#2D333C' }]
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [{ visibility: 'on' }]
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'off' }]
  },
];

let map: google.maps.Map;

export default {
  components: { Button, Input, MapPinIcon, MarkerIcon, LoadingSpinner },
  setup() {
    const { t } = useI18n();
    const state = useAppState();
    const router = useRouter();
    const route = useRoute();

    if (!state.access) {
      // Send client to start page if user and org is not created
      if (!state.createdOrganization && !state.createdUser) router.push({ name: 'start' });

      // Return user to term page if terms not accepted
      if (state.createdOrganization && !state.termsAcceptet) router.push({ name: 'terms' });

      // Send client to onboard company page if org not created
      if (state.createdUser && !state.createdOrganization) router.push({ name: 'connect' });
    } else if (!state.selectedOrganizationId) {
      router.push({ name: 'start' });
    }

    const tag = state.configTag;

    const mapOptions = {
      center: {
        lat: tag?.config?.gpsLat || DEFAULT_LATITUDE,
        lng: tag?.config?.gpsLong ||  DEFAULT_LONGITUDE
      },
      fullscreenControl: false,
      panControl: true,
      rotateControl: false,
      scaleControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: false,
      gestureHandling: 'gready',
      zoom: 17,
      styles: mapStyling
    };

    // eslint-disable-next-line no-undef
    const setMapPosition = (position: GeolocationPosition) =>
      map.setCenter({ lat: position.coords.latitude, lng: position.coords.longitude });

    const busySending = ref<boolean>(false);
    const deviceName = ref<string>(state.meta?.unitName || '');
    const locationName = ref<string>(state.meta?.locationName || '');
    const locationAddress = ref<string>(state.meta?.locationAddress || '');

    const nameValid = ref<boolean>(true);
    const locationValid = ref<boolean>(true);

    const handleDone = async () => {
      nameValid.value = !!deviceName.value;
      locationValid.value = !!locationName.value;

      try {
        if (locationName.value && deviceName.value) {
          const coords = map.getCenter();
          const lat = coords?.lat() ?? 0 + getXOffset();
          const lng = coords?.lng();

          state.setDeviceConfig({
            locationDescription: locationAddress.value || 'N/A',
            locationName: locationName.value,
            gpsLat: lat as number,
            gpsLong: lng as number,
            deviceName: deviceName.value
          });

          busySending.value = true;
          const success = await state.sendUserConfig(route.params.uuid as string);
          busySending.value = false;

          if (!success) throw new Error('Unable to send configuration');

          event('unit_placed', { method: 'Google' });

          router.push({ name: 'activating', params: { uuid: route.params.uuid } });
        }
      } catch (err) {
        toast.error(t('common.somethingWentWrong'));

        setTimeout(
          () => router.push({ name: 'start', params: { uuid: route.params.uuid } }),
          REDIRECT_DELAY
        );
      }
    };

    const getXOffset = () => new google.maps.Point((70 / Math.pow(2, map.getZoom() || 0)), 0)?.x;

    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
    });

    loader
      .load()
      .then(async (google) => {
        const mapElement = document.getElementById(MAP_ELEMENT);

        if (!mapElement) throw new Error('Map element not found');

        map = new google.maps.Map(mapElement, mapOptions);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(setMapPosition);
        }
      })
      .catch(e => {
        console.error(e);
      });

    return {
      handleDone,
      locationName,
      locationAddress,
      nameValid,
      locationValid,
      deviceName,
      busySending,
    };
  }
};
</script>

<style scoped>
#map > div:first-child {
  height: 110% !important;
}
</style>

<template>
  <div class="w-full h-full relative">
    <div id='map' class="w-full h-screen"></div>

    <div class="fixed top-1/2 left-1/2 -ml-28 -mt-32">
      <div class="relative">
        <MarkerIcon class="absolute left-0 top-0"/>
        <MapPinIcon class="absolute left-[97px]"/>
      </div>
    </div>

    <div class="absolute bottom-0 left-0 w-full px-8 mb-2">
      <div class="flex flex-col space-y-4">
        <Input
          :label="$t('common.deviceName')"
          :placeholder="$t('nfc.form.enterDeviceName')"
          :valid="nameValid || !!deviceName"
          v-model="deviceName"
        />
        <Input
          :label="$t('common.location')"
          :placeholder="$t('nfc.form.enterLocationName')"
          :valid="locationValid || !!locationName"
          v-model="locationName"
        />
        <Input
          :label="$t('common.address')"
          :placeholder="$t('nfc.form.addressOptional')"
          v-model="locationAddress"
        />

        <Button
          class="my-2 h-16 bg-onboard-background"
          @click="handleDone()"
        >
          <div class="flex flex-row items-center gap-5 justify-between w-full">
            <LoadingSpinner v-if="busySending" class="w-6 h-6"/>
            <div v-else class="w-6 h-6" />

            <div>{{ $t('common.done') }}</div>

            <div class="w-6 h-6" />
          </div>
        </Button>
      </div>
    </div>
  </div>
</template>
